import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileBarChart2, Sparkles, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { REPORT } from "@/constants/testIds";
import ExportBar from "@/components/ExportBar";

export default function Reporting() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(null);
    const [output, setOutput] = useState({ title: "", body: "" });

    useEffect(() => { api.get("/reporting").then((r) => setData(r.data)); }, []);

    const generate = async (tpl) => {
        setBusy(tpl.key);
        setOutput({ title: tpl.name, body: "" });
        try {
            const { data: r } = await api.post("/ai/task", { kind: "report_generation", payload: { template: tpl.key } });
            setOutput({ title: tpl.name, body: r.result });
            toast.success(`${tpl.name} generated`);
        } catch {
            toast.error("Report generation failed");
        } finally {
            setBusy(null);
        }
    };

    const generateRiskRegister = async () => {
        setBusy("risk_register");
        setOutput({ title: "Portfolio Risk Register", body: "" });
        try {
            const { data: r } = await api.post("/ai/task", { kind: "risk_register" });
            setOutput({ title: "Portfolio Risk Register", body: r.result });
            toast.success("Risk register generated");
        } catch {
            toast.error("Risk register failed");
        } finally {
            setBusy(null);
        }
    };

    if (!data) return <div className="kicker text-slate-500">Loading reporting workspace…</div>;
    const { reports, templates } = data;

    return (
        <div className="space-y-8">
            <div>
                <div className="kicker text-[var(--sgsb-gold)]">Reporting</div>
                <h1 className="mt-2 font-display font-black text-white text-4xl tracking-tighter">Executive Reporting</h1>
                <p className="mt-2 text-slate-400 text-sm font-body max-w-2xl">
                    Generate boardroom-quality reports with GPT-5.2. All reports are grounded in the SGS Bateman dataset.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {templates.map((t) => (
                    <div key={t.key} className="glass hover-lift rounded-xl p-5">
                        <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                            <FileBarChart2 className="h-4 w-4" strokeWidth={1.5} />
                            <span className="kicker text-[10px]">Template</span>
                        </div>
                        <div className="mt-2 font-display font-black text-white text-lg tracking-tight">{t.name}</div>
                        <p className="text-slate-500 text-xs font-body mt-1">
                            Auto-composed by the SGS Bateman AI. Includes executive summary and recommendations.
                        </p>
                        <button
                            data-testid={REPORT.generateBtn(t.key)}
                            onClick={() => generate(t)}
                            disabled={busy === t.key}
                            className="mt-4 inline-flex items-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-3 py-1.5 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                        >
                            <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
                            {busy === t.key ? "Generating…" : "Generate"}
                        </button>
                    </div>
                ))}

                <div className="glass hover-lift rounded-xl p-5 border-[color:var(--sgsb-gold)]/30">
                    <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                        <ShieldAlert className="h-4 w-4" strokeWidth={1.5} />
                        <span className="kicker text-[10px]">Risk</span>
                    </div>
                    <div className="mt-2 font-display font-black text-white text-lg tracking-tight">Portfolio Risk Register</div>
                    <p className="text-slate-500 text-xs font-body mt-1">Top 10 portfolio risks with likelihood, impact and mitigation.</p>
                    <button
                        onClick={generateRiskRegister}
                        disabled={busy === "risk_register"}
                        className="mt-4 inline-flex items-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-3 py-1.5 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                    >
                        <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
                        {busy === "risk_register" ? "Generating…" : "Generate"}
                    </button>
                </div>
            </div>

            {output.body && (
                <div data-testid={REPORT.output} className="glass rounded-xl p-6 relative">
                    <div className="absolute top-0 left-6 -translate-y-1/2 px-3 py-1 rounded-full bg-[var(--sgsb-gold)] text-slate-950 kicker text-[10px]">
                        {output.title} · SGS Bateman EPCM360™
                    </div>
                    <pre className="whitespace-pre-wrap text-slate-200 font-body text-sm leading-relaxed">{output.body}</pre>
                    <ExportBar
                        title={output.title}
                        content={output.body}
                        confidentiality="Executive"
                        testIdPrefix="report-export"
                    />
                </div>
            )}

            <div className="glass rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800/60">
                    <h3 className="font-display font-black text-white tracking-tight">Recent reports</h3>
                </div>
                <table className="w-full text-sm font-body">
                    <thead>
                        <tr className="text-slate-500 kicker text-[10px]">
                            <th className="text-left px-4 py-3">Title</th>
                            <th className="text-left px-4 py-3">Author</th>
                            <th className="text-left px-4 py-3">Issued</th>
                            <th className="text-left px-4 py-3">Confidentiality</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reports.map((r) => (
                            <tr key={r.id} className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-150">
                                <td className="px-4 py-3 text-slate-100">{r.title}</td>
                                <td className="px-4 py-3 text-slate-300">{r.author}</td>
                                <td className="px-4 py-3 text-slate-400">{r.issued}</td>
                                <td className="px-4 py-3">
                                    <span className="text-[11px] rounded-full px-2 py-0.5 border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)]">
                                        {r.confidentiality}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
